import { createAction } from '@reduxjs/toolkit'
import { firstValueFrom } from 'rxjs'
import { api } from './api'
import { recentSourcesAdapter } from '../adapters'
import { recentSourcesSelector } from '../selectors'
import StoreState, { createAppAsyncThunk } from 'store/state'
import { instanceOfStream } from '../util'
import { getStream } from '../streams/actions'
import { Feed, InteractionEvent, InteractionTrackingSource, InteractionTrackingSourceType } from 'interfaces'
import { V1 as quotesApi } from '../quotes/net'
import { QuoteFeed } from 'interfaces/Content/QuoteFeed'
import { streamsAdapter } from '../adapters/recommended'
import { impersonationSelector } from 'admin/services/selectors'

const RECENT_SOURCES_PAGE_SIZE_DEFAULT = 20

export const getRecentSources = createAppAsyncThunk(
  'content/recent/get',
  async (page: number = 1, { dispatch }) => {
    const response = await firstValueFrom(dispatch(api.getRecentSources(page, RECENT_SOURCES_PAGE_SIZE_DEFAULT)))
    return {
      items: recentSourcesAdapter(response),
      page
    }
  }
)

export const getMostRecent = createAppAsyncThunk(
  'content/recent/getMostRecent',
  async (force: boolean = true, { dispatch, getState }) => {
    if (!force) {
      const storedRecent = recentSourcesSelector(getState() as StoreState)
      if (storedRecent.length !== 0) {
        const item = storedRecent[0]
        if (instanceOfStream(item as any)) {
          return dispatch(getStream({ streamId: item.id, force: false, withFeeds: true })).unwrap()
        }
        return storedRecent[0]
      }
    }

    const items = await dispatch(getRecentSources(1)).then(({ payload }: any) => payload.items)
    if (items[0].type === 'stream') {
      return dispatch(getStream({ streamId: items[0].id, force: false, withFeeds: true }))
    }
    return items[0]
  }
)

export const removeRecentlyViewed = createAppAsyncThunk(
  'content/recent/remove',
  async (
    { type, source }: { type: InteractionTrackingSourceType, source: InteractionTrackingSource },
    { dispatch }
  ) => {
    if (type === 'quote_feed') {
      await firstValueFrom(dispatch(quotesApi.deleteSavedSearch((source as QuoteFeed).id)))
    } else {
      await firstValueFrom(dispatch(api.deleteInteraction(source.id, type)))
    }
    return { type, source }
  }
)

export const getCuratedStreams = createAppAsyncThunk(
  'content/recommended/getCuratedStreams',
  async (category: 'popular' | 'industries', { dispatch }) => {
    const response = category === 'popular'
      ? await firstValueFrom(dispatch(api.getPopularStreams()))
      : await firstValueFrom(dispatch(api.getIndustriesStreams()))

    return { category, streams: streamsAdapter(response, true) }
  }
)

export const getSavedSources = createAppAsyncThunk(
  'content/recommended/getSavedSources',
  async (_, { dispatch }) => {
    const response = await firstValueFrom(dispatch(api.getSavedSources()))
    return streamsAdapter(response).filter(s => !s.protected)
  }
)

export const setFeedToSave = createAction<Feed>('content/recommended/setFeedToSave')
export const clearFeedToSave = createAction('content/recommended/clearFeedToSave')

export const trackFeedInteraction = createAppAsyncThunk(
  'content/recommended/trackFeedInteraction',
  async (args: { feedIds: string[], event: InteractionEvent, isQuoteFeed?: boolean }, { dispatch, getState }) => {
    const { feedIds, event, isQuoteFeed } = args
    const impersonate = impersonationSelector(getState() as StoreState)
    if (impersonate) {
      console.log('[tracking] Tracking is disabled when impersonating user')
      return feedIds
    }

    await firstValueFrom(dispatch(api.registerFeedInteraction(feedIds, event, isQuoteFeed)))
    return feedIds
  }
)

export const trackFileFolderInteraction = createAppAsyncThunk(
  'content/recommended/trackFolderInteraction',
  async (args: { folderIds: string[], event: InteractionEvent }, { dispatch, getState, rejectWithValue }) => {
    const { folderIds, event } = args
    const impersonate = impersonationSelector(getState() as StoreState)
    if (impersonate) {
      console.log('[tracking] Tracking is disabled when impersonating user')
      return folderIds // or return
    }

    try {
      await firstValueFrom(dispatch(api.registerFileFolderInteraction(folderIds, event)))
      return folderIds
    } catch (err: any) {
      return rejectWithValue(err?.message || 'trackFileFolderInteraction failed')
    }
  }
)

export const trackStatusIdeasCategoryInteraction = createAppAsyncThunk(
  'content/recommended/trackStatusIdeasCategoryInteraction',
  async (args: { categoryIds: string[], event: InteractionEvent }, { dispatch, getState }) => {
    const { categoryIds, event } = args
    const impersonate = impersonationSelector(getState() as StoreState)
    if (impersonate) {
      console.log('[tracking] Tracking is disabled when impersonating user')
      return
    }

    await firstValueFrom(dispatch(api.registerStatusIdeasCategoryInteraction(categoryIds, event)))
    return categoryIds
  }
)

export const addRecentSource = createAction<InteractionTrackingSource>('content/recommended/addRecentSource')
